import { useEffect, useState, useCallback } from "react";

// Captures the browser 'beforeinstallprompt' event and exposes a
// stable API for triggering the native install flow (Chrome / Edge / Arc / Android).
// On iOS Safari the event never fires; consumers should show manual
// "Add to Home Screen" instructions in that case.
let deferredEvent = null;
const listeners = new Set();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredEvent = e;
    listeners.forEach((fn) => fn(true));
  });
  window.addEventListener("appinstalled", () => {
    deferredEvent = null;
    listeners.forEach((fn) => fn(false));
    try { localStorage.setItem("yuo_pwa_installed", "1"); } catch {}
  });
}

export function useInstallPrompt() {
  const [available, setAvailable] = useState(!!deferredEvent);
  useEffect(() => {
    listeners.add(setAvailable);
    return () => listeners.delete(setAvailable);
  }, []);
  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return { outcome: "unavailable" };
    try {
      await deferredEvent.prompt();
      const choice = await deferredEvent.userChoice;
      deferredEvent = null;
      listeners.forEach((fn) => fn(false));
      return choice;
    } catch (e) {
      return { outcome: "error", error: String(e) };
    }
  }, []);
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator?.standalone === true);
  return { available, promptInstall, isStandalone };
}

// iOS detection so we can show Safari-specific instructions.
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
}
